import {
  AfterViewInit,
  Component,
  ComponentRef,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { firstValueFrom, take } from 'rxjs';
import {
  AddCameraWizardComponent,
  AddCameraWizardData,
} from '@modals/add-camera-wizard/add-camera-wizard.component';
import { AddCameraWizardModule } from '@modals/add-camera-wizard/add-camera-wizard.module';
import { AddCameraWizardModels } from '@modals/add-camera-wizard/models/wizard.models';
import { LocationModel } from '../../../../../locations/location.model';
import { LocationSelectors } from '@states/location/location.selector-types';
import { IntelligenceModels } from '@models/intelligence.model';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';

class InlineCameraWizardDialogRef {
  public closeHandler: (created?: boolean) => void = (): void => undefined;

  public close(created?: boolean): void {
    this.closeHandler(created);
  }
}

@Component({
  selector: 'app-add-camera-result',
  standalone: true,
  imports: [AddCameraWizardModule],
  templateUrl: './add-camera-result.component.html',
  styleUrl: './add-camera-result.component.scss',
})
export class AddCameraResultComponent implements AfterViewInit, OnDestroy {
  public readonly request = input<IntelligenceModels.CameraProvisioning.AddCameraRequest | null>(null);

  private readonly wizardContainer = viewChild('wizardContainer', {
    read: ViewContainerRef,
  });
  private readonly injector = inject(Injector);
  private readonly store$ = inject(Store);
  private readonly intelligenceResultsService = inject(IntelligenceResultsService);
  private wizardComponentRef: ComponentRef<AddCameraWizardComponent> | null = null;
  private openedRequestId: string | null = null;
  private selectionSyncTimer: number | null = null;
  private viewInitialized = false;

  constructor() {
    effect(() => {
      const request = this.request();
      const wizardContainer = this.wizardContainer();
      if (!request || !wizardContainer || !this.viewInitialized || request.requestId === this.openedRequestId) {
        return;
      }
      this.openedRequestId = request.requestId;
      void this.mountWizard(request, wizardContainer);
    });
  }

  public ngAfterViewInit(): void {
    this.viewInitialized = true;
    const request = this.request();
    const wizardContainer = this.wizardContainer();
    if (!request || !wizardContainer || request.requestId === this.openedRequestId) return;
    this.openedRequestId = request.requestId;
    void this.mountWizard(request, wizardContainer);
  }

  public ngOnDestroy(): void {
    if (this.selectionSyncTimer !== null) {
      window.clearInterval(this.selectionSyncTimer);
    }
    this.wizardComponentRef?.destroy();
  }

  private async mountWizard(
    request: IntelligenceModels.CameraProvisioning.AddCameraRequest,
    wizardContainer: ViewContainerRef,
  ): Promise<void> {
    const locations = await firstValueFrom(
      this.store$.select(LocationSelectors.selectAllLocations).pipe(take(1)),
    );
    if (this.request()?.requestId !== request.requestId) return;

    const wizardData = this.createWizardData(request, locations);
    const dialogRef = new InlineCameraWizardDialogRef();
    dialogRef.closeHandler = (created?: boolean): void =>
      this.onWizardClosed(request, wizardData, created);
    const wizardInjector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: MAT_DIALOG_DATA, useValue: wizardData },
        { provide: MatDialogRef, useValue: dialogRef },
      ],
    });

    this.wizardComponentRef = wizardContainer.createComponent(AddCameraWizardComponent, {
      injector: wizardInjector,
    });
    this.wizardComponentRef.location.nativeElement.classList.add(
      'add-camera-result__wizard',
    );
    if (request.edgeId) {
      this.wizardComponentRef.instance.onCoreSelected([request.edgeId]);
      this.wizardComponentRef.instance.currentStep =
        AddCameraWizardModels.WizardStep.DetectionMethod;
    }
    this.wizardComponentRef.changeDetectorRef.detectChanges();
    this.selectionSyncTimer = window.setInterval(
      () =>
        this.syncSelectedLocation(
          this.wizardComponentRef?.instance.selectedEdgeId ?? null,
          locations,
          wizardData,
        ),
      50,
    );
  }

  private onWizardClosed(
    request: IntelligenceModels.CameraProvisioning.AddCameraRequest,
    wizardData: AddCameraWizardData,
    created: boolean | undefined,
  ): void {
    if (this.selectionSyncTimer !== null) {
      window.clearInterval(this.selectionSyncTimer);
      this.selectionSyncTimer = null;
    }
    this.wizardComponentRef?.destroy();
    this.wizardComponentRef = null;
    this.openedRequestId = null;
    if (created) {
      this.intelligenceResultsService.resolveAddCamera(
        request.requestId,
        wizardData.locationCore.name ?? '',
      );
      return;
    }
    this.intelligenceResultsService.cancelAddCamera(request.requestId);
  }

  private syncSelectedLocation(
    edgeId: string | null,
    locations: LocationModel.LocationItem[],
    wizardData: AddCameraWizardData,
  ): void {
    if (!edgeId) return;
    const selectedLocation = locations.find((location) =>
      Object.values(location.edges ?? {}).some((edge) => edge.edgeId === edgeId),
    );
    if (!selectedLocation?._id) return;
    wizardData.locationId = selectedLocation._id;
    wizardData.locationCore = selectedLocation;
  }

  private createWizardData(
    request: IntelligenceModels.CameraProvisioning.AddCameraRequest,
    locations: LocationModel.LocationItem[],
  ): AddCameraWizardData {
    const locationCore = locations.find((location) => location._id === request.locationId) ?? {};
    return {
      locationId: request.locationId ?? '',
      locationCore,
    };
  }
}
